//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import axios from 'axios';

import { type ApiClient } from './apiClient';
import {
  EXPOSURE_LEVELS, type AgentDebugSettings, type ExposureLevel,
  type LevelEntry, type PermissionProfile, type PermissionProfilesResponse,
  type ProfileDraft
} from './apiTypes';

export class ProfileConflictError extends Error {
  constructor(readonly currentVersion: number) {
    super('The profile was saved elsewhere; it is now at version ' +
          `${currentVersion}.`);
    this.name = 'ProfileConflictError';
  }
}

const isLevel = (value: unknown): value is ExposureLevel =>
  typeof value === 'string' &&
  (EXPOSURE_LEVELS as readonly string[]).includes(value);

const isNameOrNull = (value: unknown): value is string | null =>
  value === null || (typeof value === 'string' && value !== '');

// A property level needs its frame: `frame: null` addresses the graph
// itself, so a non-null `property` alongside it names no real element.
const isEntry = (value: unknown): value is LevelEntry => {
  if (value === null || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  return typeof e.graph === 'string' && e.graph !== '' &&
    isNameOrNull(e.frame) && isNameOrNull(e.property) && isLevel(e.level) &&
    !(e.frame === null && e.property !== null);
};

// Loud on a bad shape: an editor that silently drops an entry it cannot
// read would save the profile back without it.
const assertProfile = (value: unknown, label: string): PermissionProfile => {
  if (value === null || typeof value !== 'object')
    throw new Error(`${label}: expected a profile object`);
  const p = value as Record<string, unknown>;
  if (typeof p.name !== 'string' || !Number.isInteger(p.version) ||
      !isLevel(p.default_level) ||
      !Number.isInteger(p.cardinality_threshold) ||
      !Array.isArray(p.entries) || !p.entries.every(isEntry) ||
      !Array.isArray(p.assigned_models) ||
      !p.assigned_models.every((m) => typeof m === 'string') ||
      (p.modified_by !== null && typeof p.modified_by !== 'string') ||
      (p.modified_at !== null && typeof p.modified_at !== 'string'))
    throw new Error(`${label}: a profile has an unknown level or a ` +
                    'missing field');
  if (p.name === '') throw new Error(`${label}: a profile has an empty name`);
  if (Number(p.cardinality_threshold) < 1)
    throw new Error(`${label}: a profile's cardinality threshold is below 1`);
  return value as PermissionProfile;
};

const assertProfiles = (
  value: unknown, label: string
): PermissionProfilesResponse => {
  const list = (value as { profiles?: unknown } | null)?.profiles;
  if (!Array.isArray(list)) throw new Error(`${label}: expected profiles`);
  return { profiles: list.map((p) => assertProfile(p, label)) };
};

const assertAgentDebug = (
  value: unknown, label: string
): AgentDebugSettings => {
  const body = value as Partial<Record<keyof AgentDebugSettings, unknown>> |
    null;
  const flag = (name: keyof AgentDebugSettings): boolean => {
    const on = body?.[name];
    if (typeof on !== 'boolean')
      throw new Error(`${label}: expected a boolean ${name}`);
    return on;
  };
  return {
    record_summary_jobs: flag('record_summary_jobs'),
    allow_create_and_load: flag('allow_create_and_load')
  };
};

const rethrowConflict = (error: unknown): never => {
  if (axios.isAxiosError(error) && error.response?.status === 409) {
    const version: unknown =
      (error.response.data as { current_version?: unknown } | null)
        ?.current_version;
    if (typeof version === 'number') throw new ProfileConflictError(version);
  }
  throw error;
};

/**
 * ADEL profiles: what each AI model may see of each graph, and the page's
 * agent debugging switch.  Admin-only on the backend.
 */
export class PermissionProfileService {
  constructor(private readonly api: ApiClient) {}

  getProfiles = async (): Promise<PermissionProfilesResponse> =>
    assertProfiles(await this.api.makeRequest<unknown>(
      'admin/permission_profiles', 'get'), 'permission profiles');

  createProfile = async (draft: ProfileDraft): Promise<PermissionProfile> => {
    try {
      return assertProfile(await this.api.makeRequest<unknown>(
        'admin/permission_profiles', 'post', draft), 'profile create');
    } catch (error) {
      return rethrowConflict(error);
    }
  };

  saveProfile = async (
    draft: ProfileDraft, expectedVersion: number
  ): Promise<PermissionProfile> => {
    try {
      return assertProfile(await this.api.makeRequest<unknown>(
        `admin/permission_profiles/${encodeURIComponent(draft.name)}`, 'put',
        { ...draft, expected_version: expectedVersion }), 'profile save');
    } catch (error) {
      return rethrowConflict(error);
    }
  };

  deleteProfile = async (name: string): Promise<PermissionProfilesResponse> =>
    assertProfiles(await this.api.makeRequest<unknown>(
      `admin/permission_profiles/${encodeURIComponent(name)}`, 'delete'),
    'profile delete');

  getAgentDebug = async (): Promise<AgentDebugSettings> =>
    assertAgentDebug(await this.api.makeRequest<unknown>(
      'admin/agent_debug', 'get'), 'agent debug settings');

  /** Sets the switches named in `patch`; the server leaves the rest as they
   *  are and answers with every switch. */
  setAgentDebug = async (
    patch: Partial<AgentDebugSettings>
  ): Promise<AgentDebugSettings> =>
    assertAgentDebug(await this.api.makeRequest<unknown>(
      'admin/agent_debug', 'put', patch), 'agent debug save');
}
